import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Briefcase, RefreshCw, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Discover() {
  const navigate = useNavigate();
  const [persona, setPersona] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const findMystery = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.post("/mystery-match");
      setPersona(res.data);
    } catch { toast.error("Couldn't find a persona right now. Please try again."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { findMystery(); }, [findMystery]);

  const startChat = async () => {
    if (!persona || starting) return;
    setStarting(true);
    try {
      await api.post(`/chats/${persona.id}/start`);
      navigate(`/app/chat/${persona.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.reason === "persona_cap") {
        toast.error(`You already have ${detail.limit} AI personas. Delete one from Chats to start another.`);
        navigate("/app/chats");
      } else toast.error("Couldn't start the chat. Please try again.");
      setStarting(false);
    }
  };

  const initials = persona ? persona.name.split(" ").map((p) => p[0]).slice(0, 2).join("") : "";

  return (
    <div className="mx-auto max-w-lg px-5 py-8" data-testid="discover-page">
      <div className="flex items-center gap-2 text-[#D4AF37]"><Sparkles className="h-4 w-4" /><span className="text-xs uppercase tracking-[0.2em]">Mystery Match</span></div>
      <h1 className="mt-2 font-display text-3xl text-[#F4F1EA]">Meet an AI Persona</h1>
      <p className="mt-1 flex items-center gap-2 text-sm text-[#C8C2B5]">A fictional AI adult, matched to your preference.
        <span className="rounded-full bg-[#24201C] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#D4AF37]">AI persona</span></p>

      <div className="mt-6 rounded-2xl border border-[#2B241B] bg-[#12100E] p-6" data-testid="mystery-card">
        {loading || !persona ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" /></div>
        ) : (
          <motion.div key={persona.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex flex-col items-center text-center">
              <div className="flex h-24 w-24 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-gradient-to-br from-[#2A2318] to-[#0A0908] font-display text-3xl text-[#D4AF37]">{initials}</div>
              <h2 className="mt-4 font-display text-2xl text-[#F4F1EA]">{persona.name.split(" ")[0]}, {persona.age}</h2>
              <p className="mt-1 flex items-center gap-1 text-sm text-[#C8C2B5]"><MapPin className="h-3 w-3" /> {persona.living_in}</p>
              <p className="mt-1 flex items-center gap-1 text-sm text-[#8C8578]"><Briefcase className="h-3 w-3" /> {persona.occupation}</p>
              <div className="mt-3 flex flex-wrap justify-center gap-1.5">{(persona.traits || []).slice(0, 3).map((t) => <span key={t} className="rounded-full bg-[#1A1714] px-3 py-1 text-xs text-[#C8C2B5]">{t}</span>)}</div>
              <p className="mt-4 text-xs text-[#8C8578]">The rest of their story unfolds naturally if you choose to chat.</p>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-2">
              <Button variant="outline" data-testid="try-another" onClick={findMystery} className="rounded-full border-[#3D3428] bg-transparent text-[#C8C2B5] hover:bg-[#1A1714] hover:text-[#F4F1EA]"><RefreshCw className="mr-2 h-4 w-4" />Try another</Button>
              <Button data-testid="start-persona" onClick={startChat} disabled={starting} className="rounded-full bg-[#D4AF37] font-semibold text-[#0A0908] hover:bg-[#E5C158]">{starting ? "Starting…" : "Start chat"}</Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
